// @ts-strict-ignore
import * as React from 'react';
import { AutomationType } from '../../types';
import { useDispatch, useSelector } from 'react-redux';
import { BuildDistributionUpdatable } from '../../actions/updatable';

import { RootState } from '../../reducers/reducers';
import { selectBuildDistributionBucketById, selectIsDemoInstanceOrDemoModeEnabled } from '../../selectors/selectors';
import { useGetBuildDistroBucketAutomationSettingFieldsQuery } from '@/middlewares/defaultApi';
import { BucketComponentProps } from '../../typescriptHelpers';
import { globalMessage } from '../../utils/globalMessage';
import { tryUpdateBuildDistributionBucket } from '../../actions/buildDistribution';
import { AutomationSettings } from './AutomationSettings';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { AutomationSettingsContext } from '@/helpers/automationHelpers';
import { configFileSectionForBucket } from '../HOCs/ConfigFileManagedMessage';

interface BucketAutomationSettingsProps extends BucketComponentProps {
    open: boolean;
    automationType: AutomationType;
    onClose: () => void;
    onAfterClose: () => void;
}

export const BucketAutomationSettings: React.FC<BucketAutomationSettingsProps> = (props) => {
    const dispatch = useDispatch();

    const bucket = useSelector((state: RootState) => selectBuildDistributionBucketById(state, props.bucketId));
    const bucketAutomations = bucket?.automations ?? [];
    const automation = bucketAutomations.find((automation) => automation.type === props.automationType);

    const isDemoMode = useSelector((state: RootState) => selectIsDemoInstanceOrDemoModeEnabled(state, props.orgId));
    const { data: settingFieldsResponse, isError: isSettingFieldsError } =
        useGetBuildDistroBucketAutomationSettingFieldsQuery(
            { orgId: props.orgId, appId: props.appId, bucketId: props.bucketId },
            { refetchOnMountOrArgChange: false, skip: !props.open || isDemoMode },
        );
    const settingFields = isDemoMode ? [] : settingFieldsResponse?.data?.[props.automationType];

    const updateBucketAutomationEnabledIsLoading = useIsLoading(
        BuildDistributionUpdatable.UpdateBuildDistroBucketAutomationSettings,
        bucket?.id + automation?.type,
    );

    const updateBucketAutomationEnabledSuccess = useUpdated(
        BuildDistributionUpdatable.UpdateBuildDistroBucketAutomationSettings,
        bucket?.id + automation?.type,
    );
    const updateBucketAutomationEnabledError = useError(
        BuildDistributionUpdatable.UpdateBuildDistroBucketAutomationSettings,
        bucket?.id + automation?.type,
    );

    React.useEffect(() => {
        if (updateBucketAutomationEnabledSuccess) {
            globalMessage.success('Updated bucket automation settings!');

            props.onClose();
        }
    }, [updateBucketAutomationEnabledSuccess]);

    React.useEffect(() => {
        if (updateBucketAutomationEnabledError) {
            globalMessage.error(updateBucketAutomationEnabledError);
        }
    }, [updateBucketAutomationEnabledError]);

    return (
        automation && (
            <AutomationSettings
                {...props}
                onAutomationSelected={null}
                sameTypeAutomations={[]}
                automation={automation}
                isLoading={updateBucketAutomationEnabledIsLoading}
                context={AutomationSettingsContext.App}
                settingFields={settingFields}
                isSettingFieldsError={isSettingFieldsError}
                configFileSection={configFileSectionForBucket(bucket)}
                onSubmit={(request) => {
                    dispatch(
                        tryUpdateBuildDistributionBucket(
                            BuildDistributionUpdatable.UpdateBuildDistroBucketAutomationSettings,
                            {
                                ...request,
                                bucketId: bucket.id,
                            },
                        ),
                    );
                }}
            />
        )
    );
};
